import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, Heart, User as UserIcon, KeyRound, LogOut, RotateCcw, Building2, XCircle, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtTRY, fmtDate, fmtQty4  } from "../lib/format";
import { errText } from "../lib/errors";
import { cargoTrackUrl } from "../lib/cargo";
import ProductCard from "../components/ProductCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const STATUS_COLORS = {
  "Yeni Sipariş": "bg-blue-100 text-blue-800", "Onay Bekliyor": "bg-amber-100 text-amber-800",
  "Hazırlanıyor": "bg-indigo-100 text-indigo-800", "Kargoya Verildi": "bg-purple-100 text-purple-800",
  "Teslim Edildi": "bg-green-100 text-green-800", "İptal Edildi": "bg-red-100 text-red-700",
  "İptal Talebi": "bg-orange-100 text-orange-800",
  "İade Talebi": "bg-orange-100 text-orange-800", "İade Edildi": "bg-stone-200 text-stone-700",
};

export default function Account() {
  const { user, loading, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [openOrder, setOpenOrder] = useState("");
  const [addrEdit, setAddrEdit] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/giris");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name, phone: user.phone || "" });
    api.get("/orders/my").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/favorites").then(({ data }) => setFavorites(data)).catch(() => {});
  }, [user]);

  if (!user) return null;

  const saveProfile = async () => {
    await api.put("/auth/profile", profile);
    await refresh();
    toast.success("Profil güncellendi");
  };

  const changePw = async () => {
    try {
      await api.put("/auth/password", pw);
      toast.success("Şifreniz değiştirildi");
      setPw({ current_password: "", new_password: "" });
    } catch (err) { toast.error(errText(err) || "Şifre değiştirilemedi"); }
  };

  const requestReturn = async (orderNumber) => {
    const reason = window.prompt("İade sebebinizi kısaca yazın:");
    if (!reason) return;
    try {
      await api.post(`/orders/${orderNumber}/return`, { reason });
      toast.success("İade talebiniz alındı");
      const { data } = await api.get("/orders/my");
      setOrders(data);
    } catch (err) { toast.error(errText(err) || "İade talebi oluşturulamadı"); }
  };

  const reloadOrders = async () => { const { data } = await api.get("/orders/my"); setOrders(data); };

  const cancelOrder = async (o) => {
    const paid = o.payment_status === "paid";
    const reason = window.prompt(paid ? "Ödemesi alınan sipariş için iptal talebi oluşturulacak, iade süreci mağaza onayıyla yürütülür. İptal sebebinizi yazın:" : "Sipariş anında iptal edilecek ve stoklar geri eklenecek. İptal sebebinizi yazın:");
    if (reason === null) return;
    try {
      const { data } = await api.post(`/orders/${o.order_number}/cancel`, { reason });
      toast.success(data.mode === "talep" ? "İptal talebiniz alındı — mağaza onayı sonrası iade edilir" : "Siparişiniz iptal edildi");
      reloadOrders();
    } catch (err) { toast.error(errText(err) || "İptal edilemedi"); }
  };

  const saveAddress = async (o) => {
    try {
      await api.put(`/orders/${o.order_number}/address`, addrEdit);
      toast.success("Teslimat adresi güncellendi");
      setAddrEdit(null);
      reloadOrders();
    } catch (err) { toast.error(errText(err) || "Adres güncellenemedi"); }
  };

  const EARLY = ["Yeni Sipariş", "Onay Bekliyor"];

  const inputCls = "w-full h-11 px-4 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

  return (
    <div data-testid="account-page" className="max-w-6xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-3xl text-forest">Merhaba, {user.name}</h1>
          <p className="text-stone-500 text-sm mt-1">{user.email} {user.role === "b2b" && <span className="ml-2 bg-gold/20 text-gold-dark text-xs font-bold px-2.5 py-1 rounded-full">B2B Üye</span>}</p>
        </div>
        <div className="flex gap-2">
          {(user.role === "admin" || user.role === "super_admin") && <Link to="/admin" data-testid="admin-panel-link" className="bg-stone-900 text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-stone-800 transition-colors">Yönetim Paneli</Link>}
          <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-2 border border-stone-300 text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-cream-dark transition-colors"><LogOut className="w-4 h-4" /> Çıkış</button>
        </div>
      </div>

      {user.role === "b2b" && (
        <Link to="/b2b" className="mt-6 flex items-center gap-3 bg-gold/10 border border-gold/40 rounded-2xl p-4 hover:bg-gold/20 transition-colors">
          <Building2 className="w-6 h-6 text-gold-dark" />
          <div><p className="font-semibold text-stone-900 text-sm">B2B Paneliniz aktif</p><p className="text-xs text-stone-600">Teklif talepleri ve toplu sipariş için tıklayın</p></div>
        </Link>
      )}

      <Tabs defaultValue="orders" className="mt-8">
        <TabsList className="bg-cream-dark/70 flex-wrap h-auto gap-1 p-1.5 rounded-xl">
          <TabsTrigger data-testid="account-tab-orders" value="orders" className="rounded-lg"><Package className="w-4 h-4 mr-1.5" /> Siparişlerim</TabsTrigger>
          <TabsTrigger data-testid="account-tab-favorites" value="favorites" className="rounded-lg"><Heart className="w-4 h-4 mr-1.5" /> Favorilerim</TabsTrigger>
          <TabsTrigger data-testid="account-tab-profile" value="profile" className="rounded-lg"><UserIcon className="w-4 h-4 mr-1.5" /> Profil</TabsTrigger>
          <TabsTrigger data-testid="account-tab-password" value="password" className="rounded-lg"><KeyRound className="w-4 h-4 mr-1.5" /> Şifre</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6 space-y-4">
          {orders.length === 0 && <p className="text-stone-500 text-sm bg-white rounded-2xl border border-stone-200/80 p-8 text-center">Henüz siparişiniz yok.</p>}
          {orders.map((o) => (
            <div key={o.order_id} data-testid={`order-card-${o.order_number}`} className="bg-white rounded-2xl border border-stone-200/80 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono font-bold text-stone-900">{o.order_number}</p>
                  <p className="text-xs text-stone-500 mt-0.5">{fmtDate(o.created_at)} · {o.items.length} kalem</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${STATUS_COLORS[o.status] || "bg-stone-100"}`}>{o.status}</span>
                  <p className="font-bold text-forest">{fmtTRY(o.total)}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-4 flex-wrap">
                {o.items.slice(0, 4).map((i) => <img key={i.product_id + (i.variant || "")} src={i.image} alt={i.name} title={i.name} className="w-12 h-12 rounded-lg object-cover bg-cream-dark" />)}
                {o.items.length > 4 && <span className="w-12 h-12 rounded-lg bg-cream-dark flex items-center justify-center text-xs font-bold text-stone-500">+{o.items.length - 4}</span>}
              </div>
              {o.tracking_number && (
                <p className="text-xs text-stone-600 mt-3 flex flex-wrap items-center gap-2">
                  <span>Kargo: <b>{o.cargo_company}</b> · Takip No: <b className="font-mono">{o.tracking_number}</b></span>
                  {cargoTrackUrl(o.cargo_company, o.tracking_number) && (
                    <a data-testid={`track-cargo-${o.order_number}`} href={cargoTrackUrl(o.cargo_company, o.tracking_number)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1B4332] text-white text-[11px] font-semibold hover:bg-[#143325] transition-colors">
                      Kargoyu Takip Et
                    </a>
                  )}
                </p>
              )}
              {o.cancel_reason && <p className="text-xs text-terra mt-2">İptal sebebi: {o.cancel_reason}</p>}

              <div className="flex flex-wrap gap-2 mt-4">
                <button data-testid={`order-detail-${o.order_number}`} onClick={() => setOpenOrder(openOrder === o.order_number ? "" : o.order_number)} className="flex items-center gap-1.5 text-xs font-semibold border border-stone-300 hover:border-forest text-stone-700 px-3.5 py-2 rounded-lg transition-colors">
                  {openOrder === o.order_number ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} Detay
                </button>
                {EARLY.includes(o.status) && (
                  <>
                    <button data-testid={`order-address-edit-${o.order_number}`} onClick={() => setAddrEdit({ order: o.order_number, full_name: o.address?.full_name || "", phone: o.address?.phone || "", city: o.address?.city || "", district: o.address?.district || "", address: o.address?.address || "", postal_code: o.address?.postal_code || "" })} className="flex items-center gap-1.5 text-xs font-semibold border border-stone-300 hover:border-forest text-stone-700 px-3.5 py-2 rounded-lg transition-colors"><MapPin className="w-3.5 h-3.5" /> Adresi Düzenle</button>
                    <button data-testid={`order-cancel-${o.order_number}`} onClick={() => cancelOrder(o)} className="flex items-center gap-1.5 text-xs font-semibold bg-red-50 border border-red-200 hover:bg-red-100 text-red-700 px-3.5 py-2 rounded-lg transition-colors"><XCircle className="w-3.5 h-3.5" /> Siparişi İptal Et</button>
                  </>
                )}
                {o.status === "Teslim Edildi" && (
                  <button data-testid={`return-btn-${o.order_number}`} onClick={() => requestReturn(o.order_number)} className="flex items-center gap-1.5 text-xs font-semibold text-terra hover:underline"><RotateCcw className="w-3.5 h-3.5" /> İade Talebi Oluştur</button>
                )}
              </div>

              {openOrder === o.order_number && (
                <div data-testid={`order-detail-panel-${o.order_number}`} className="mt-4 border-t border-stone-100 pt-4 space-y-3">
                  <div className="space-y-1.5">
                    {o.items.map((i) => (
                      <div key={i.product_id + (i.variant || "")} className="flex justify-between text-sm">
                        <span className="text-stone-700">{i.name} {i.variant ? `(${i.variant})` : ""} × {fmtQty4(i.quantity, i.unit || "")}</span>
                        <span className="font-semibold text-stone-900">{fmtTRY(i.total)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs text-stone-500 pt-1 border-t border-stone-100"><span>Kargo</span><span>{o.shipping_cost > 0 ? fmtTRY(o.shipping_cost) : "Ücretsiz"}</span></div>
                    {o.discount > 0 && <div className="flex justify-between text-xs text-emerald-700"><span>İndirim {o.coupon_code ? `(${o.coupon_code})` : ""}</span><span>-{fmtTRY(o.discount)}</span></div>}
                  </div>
                  <div className="bg-cream-dark/50 rounded-xl p-3 text-xs text-stone-600">
                    <p className="font-semibold text-stone-700 mb-0.5">Teslimat Adresi</p>
                    <p>{o.address?.full_name} — {o.address?.phone}</p>
                    <p>{o.address?.address} {o.address?.district} {o.address?.city} {o.address?.postal_code}</p>
                  </div>
                  {(o.status_history || []).length > 0 && (
                    <div className="text-xs text-stone-400 space-y-0.5">
                      {o.status_history.slice(-4).map((h, i2) => <p key={i2}>· {h.status} — {fmtDate(h.at)}</p>)}
                    </div>
                  )}
                </div>
              )}

              {addrEdit && addrEdit.order === o.order_number && (
                <div data-testid={`order-address-form-${o.order_number}`} className="mt-4 bg-cream-dark/40 border border-stone-200 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input data-testid="addr-full-name" className={inputCls} placeholder="Ad Soyad" value={addrEdit.full_name} onChange={(e) => setAddrEdit({ ...addrEdit, full_name: e.target.value })} />
                  <input data-testid="addr-phone" className={inputCls} placeholder="Telefon" value={addrEdit.phone} onChange={(e) => setAddrEdit({ ...addrEdit, phone: e.target.value })} />
                  <input data-testid="addr-city" className={inputCls} placeholder="İl" value={addrEdit.city} onChange={(e) => setAddrEdit({ ...addrEdit, city: e.target.value })} />
                  <input data-testid="addr-district" className={inputCls} placeholder="İlçe" value={addrEdit.district} onChange={(e) => setAddrEdit({ ...addrEdit, district: e.target.value })} />
                  <input data-testid="addr-address" className={`${inputCls} sm:col-span-2`} placeholder="Açık adres" value={addrEdit.address} onChange={(e) => setAddrEdit({ ...addrEdit, address: e.target.value })} />
                  <div className="sm:col-span-2 flex gap-2">
                    <button data-testid={`addr-save-${o.order_number}`} onClick={() => saveAddress(o)} className="bg-forest hover:bg-forest-dark text-white text-xs font-bold px-5 py-2.5 rounded-lg transition-colors">Adresi Kaydet</button>
                    <button onClick={() => setAddrEdit(null)} className="text-xs font-semibold text-stone-500 px-4 py-2.5">Vazgeç</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="favorites" className="mt-6">
          {favorites.length === 0 ? <p className="text-stone-500 text-sm bg-white rounded-2xl border border-stone-200/80 p-8 text-center">Favori ürününüz yok.</p> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">{favorites.map((p) => <ProductCard key={p.product_id} product={p} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 max-w-md space-y-4">
            <div><label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Ad Soyad</label><input data-testid="profile-name" className={`${inputCls} mt-1.5`} value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Telefon</label><input data-testid="profile-phone" className={`${inputCls} mt-1.5`} value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
            <button data-testid="profile-save" onClick={saveProfile} className="bg-forest hover:bg-forest-dark text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors">Kaydet</button>
          </div>
        </TabsContent>

        <TabsContent value="password" className="mt-6">
          <div className="bg-white rounded-2xl border border-stone-200/80 p-6 max-w-md space-y-4">
            <div><label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Mevcut Şifre</label><input data-testid="current-password" type="password" className={`${inputCls} mt-1.5`} value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></div>
            <div><label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">Yeni Şifre</label><input data-testid="new-password" type="password" className={`${inputCls} mt-1.5`} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></div>
            <button data-testid="password-save" onClick={changePw} className="bg-forest hover:bg-forest-dark text-white font-semibold px-6 py-2.5 rounded-xl text-sm transition-colors">Şifreyi Değiştir</button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
